import React, { useEffect, useState } from 'react';
import i18n, { SUPPORTED_LOCALES } from '../i18n';

/**
 * First-visit locale nudge.
 *
 * Detects the browser language and, if we ship a matching locale that differs
 * from what the user is currently seeing, drops a friendly one-line prompt at
 * the top of the app: "Envie d'utiliser Clanster en français ?" (in the target
 * language itself — so users recognise the invitation even if their browser
 * language differs from Clanster's default).
 *
 * Rules:
 *  • Never nudges English speakers or users whose browser language we don't
 *    ship — they wouldn't gain anything.
 *  • Shows exactly once per browser. Answer (accept / dismiss) is persisted in
 *    localStorage under `clanster_locale_nudge_seen`.
 *  • Also treats a user who already picked a language explicitly (localStorage
 *    `i18nextLng` is set to any code) as "no nudge needed" — respects their
 *    intent.
 */
const STORAGE_KEY = 'clanster_locale_nudge_seen';

const detectTargetLocale = () => {
  const supported = SUPPORTED_LOCALES.map((l) => l.code);
  // navigator.languages is ordered by preference — pick the first that matches.
  const langs = (navigator?.languages || [navigator?.language || 'en']).map((l) =>
    String(l).toLowerCase().split('-')[0]
  );
  for (const l of langs) {
    if (supported.includes(l) && l !== 'en') return l;
  }
  return null;
};

export default function LocaleNudge() {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    // Already dismissed / accepted in a prior visit → skip.
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch { /* localStorage disabled → still show once */ }

    // User already picked a language explicitly via the switcher → skip.
    // (i18next stores their choice in localStorage under 'i18nextLng'.)
    let currentLng = 'en';
    try {
      currentLng = (localStorage.getItem('i18nextLng') || i18n.language || 'en').split('-')[0];
    } catch { /* ignore */ }
    if (currentLng !== 'en') return;

    const detected = detectTargetLocale();
    if (!detected || detected === currentLng) return;

    setTarget(detected);
  }, []);

  if (!target) return null;

  // Pull nudge copy in the DETECTED language (not the app's current language)
  // so the invitation is instantly recognisable.
  const tFixed = i18n.getFixedT(target);
  const meta = SUPPORTED_LOCALES.find((l) => l.code === target);

  const persist = (value) => {
    try { localStorage.setItem(STORAGE_KEY, value); } catch { /* noop */ }
  };

  const accept = () => {
    i18n.changeLanguage(target);
    persist(target);
    setTarget(null);
  };

  const dismiss = () => {
    persist('dismissed');
    setTarget(null);
  };

  return (
    <div className="locale-nudge" role="dialog" aria-live="polite" data-testid="locale-nudge">
      <span className="locale-nudge-flag" aria-hidden="true">{tFixed('localeNudge.flag')}</span>
      <span className="locale-nudge-copy" data-testid="locale-nudge-copy">
        {tFixed('localeNudge.question')}
      </span>
      <button
        type="button"
        className="locale-nudge-btn accept"
        onClick={accept}
        data-testid="locale-nudge-accept"
      >
        {tFixed('localeNudge.switchCta')} <b>{meta?.native || target}</b>
      </button>
      <button
        type="button"
        className="locale-nudge-btn stay"
        onClick={dismiss}
        data-testid="locale-nudge-stay"
      >
        {tFixed('localeNudge.stayCta')}
      </button>
      <button
        type="button"
        className="locale-nudge-close"
        aria-label={tFixed('localeNudge.dismissAria')}
        onClick={dismiss}
        data-testid="locale-nudge-close"
      >
        <svg viewBox="0 0 16 16" fill="none" width="12" height="12">
          <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
